import React, { Fragment, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Fade, useTheme } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import { MessageItem } from './MessageItem';
import { useVisibleDate } from '../../hooks';
import { formatDateLabel } from '../../utils';
import { DateLabel } from './DateLabel';

export const MessagesList = ({
    messages,
    conversationId,
    onLoadMore,
    hasMore = false,
    isLoadingInitial = false,
    isLoadingMore = false,
}: {
    messages: any[];
    conversationId?: string;
    onLoadMore?: () => void;
    hasMore?: boolean;
    isLoadingInitial?: boolean;
    isLoadingMore?: boolean;
}) => {
    const theme = useTheme<{
        palette: Theme['palette'] & { background: { dark: string } };
    }>();
    const { t } = useTranslation();

    const containerRef = useRef<HTMLDivElement | null>(null);
    const previousConversationIdRef = useRef<string | undefined>(null);
    const previousMessagesLengthRef = useRef(0);
    const previousScrollHeightRef = useRef(0);
    const previousScrollTopRef = useRef(0);
    const isNearBottomRef = useRef(true);
    const shouldKeepScrollPositionRef = useRef(false);
    const [hideFloating, setHideFloating] = useState(true);

    const handleScroll = () => {
        if (!containerRef.current) return;

        const { scrollTop, clientHeight, scrollHeight } = containerRef.current;

        const isTop = scrollTop === 0;
        const isBottom = scrollTop + clientHeight >= scrollHeight - 1;
        isNearBottomRef.current = scrollTop + clientHeight >= scrollHeight - 80;

        setHideFloating(isTop || isBottom);

        if (scrollTop <= 80 && hasMore && !isLoadingMore && onLoadMore) {
            previousScrollHeightRef.current = scrollHeight;
            previousScrollTopRef.current = scrollTop;
            shouldKeepScrollPositionRef.current = true;
            onLoadMore();
        }
    };

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const isNewConversation =
            previousConversationIdRef.current !== conversationId;
        const hadNoMessages = previousMessagesLengthRef.current === 0;

        if (isNewConversation || hadNoMessages) {
            container.scrollTop = container.scrollHeight;
        } else if (shouldKeepScrollPositionRef.current) {
            container.scrollTop =
                previousScrollTopRef.current +
                container.scrollHeight -
                previousScrollHeightRef.current;
            shouldKeepScrollPositionRef.current = false;
        } else if (isNearBottomRef.current) {
            container.scrollTop = container.scrollHeight;
        }

        previousConversationIdRef.current = conversationId;
        previousMessagesLengthRef.current = messages?.length;
    }, [conversationId, messages?.length]);

    const visibleDate = useVisibleDate();
    const showFloatingDate = Boolean(visibleDate && !hideFloating);
    const loadingMoreTopOffset = showFloatingDate ? 48 : 12;
    let lastDate = '';

    return (
        <div
            ref={containerRef}
            onScroll={handleScroll}
            style={{
                flex: 1,
                overflowY: 'auto',
                position: 'relative',
                height: '100%',
            }}
        >
            {visibleDate && (
                <Fade in={showFloatingDate} timeout={600}>
                    <div
                        style={{
                            position: 'sticky',
                            top: 8,
                            display: 'flex',
                            justifyContent: 'center',
                            zIndex: 20,
                            pointerEvents: 'none',
                        }}
                    >
                        <DateLabel label={visibleDate} />
                    </div>
                </Fade>
            )}

            <Fade in={isLoadingMore} timeout={200}>
                <div
                    style={{
                        position: 'absolute',
                        top: loadingMoreTopOffset,
                        left: 0,
                        right: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        zIndex: 30,
                        pointerEvents: 'none',
                    }}
                >
                    <Box
                        bgcolor={theme.palette.background.paper}
                        borderRadius={16}
                        boxShadow={2}
                        px={1.5}
                        py={1}
                    >
                        <CircularProgress size={18} />
                    </Box>
                </div>
            </Fade>

            <Box
                style={{
                    background: theme.palette.background.dark,
                    minHeight: '100%',
                }}
                p={2}
            >
                {isLoadingInitial && messages?.length === 0 && (
                    <Box
                        alignItems="center"
                        display="flex"
                        justifyContent="center"
                        minHeight="100%"
                    >
                        <CircularProgress size={28} />
                    </Box>
                )}

                {messages?.map((message) => {
                    const date = new Date(message.createdAt);
                    const label = formatDateLabel(date, t);

                    const showHeader = label !== lastDate;
                    lastDate = label;

                    return (
                        <Fragment key={message.renderKey || message.id}>
                            {showHeader && (
                                <Box
                                    data-date-header={label}
                                    display="flex"
                                    justifyContent="center"
                                    my={2}
                                >
                                    <DateLabel label={label} />
                                </Box>
                            )}

                            <MessageItem message={message} />
                        </Fragment>
                    );
                })}
            </Box>
        </div>
    );
};
